import { motion } from 'framer-motion';
import React from 'react';
import { FiActivity, FiArrowLeft, FiBarChart2, FiCheckCircle, FiFileText, FiLayers, FiSettings } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import Footer from '../../components/Footer';
import Navbar from '../../components/Navbar';

const LifeSciences = () => {
  const solutions = [
    {
      title: "Clinical Trial Management",
      description: "Comprehensive platforms for managing clinical trials from protocol design to regulatory submission.",
      icon: FiFileText,
      features: ["Protocol Management", "Patient Recruitment", "Data Collection", "Regulatory Reporting"]
    },
    {
      title: "Laboratory Information Systems",
      description: "Advanced LIMS solutions for sample tracking, data management, and workflow automation in research labs.",
      icon: FiLayers,
      features: ["Sample Tracking", "Workflow Automation", "Quality Control", "Instrument Integration"]
    },
    {
      title: "Research Data Analytics",
      description: "Powerful analytics platforms for drug discovery, biomarker identification, and clinical data analysis.",
      icon: FiBarChart2,
      features: ["Statistical Analysis", "Data Visualization", "Machine Learning", "Predictive Modeling"]
    },
    {
      title: "Regulatory Compliance Tools",
      description: "Software solutions ensuring compliance with FDA, EMA, and other regulatory requirements.",
      icon: FiCheckCircle,
      features: ["21 CFR Part 11", "Audit Trails", "Electronic Signatures", "Validation Support"]
    }
  ];

  const caseStudies = [
    {
      title: "Global Pharmaceutical Clinical Trial Platform",
      challenge: "Multinational pharma company needed unified platform for managing 50+ concurrent clinical trials",
      solution: "Implemented cloud-based CTMS with real-time monitoring and regulatory compliance features",
      results: ["40% reduction in trial management costs", "60% faster patient recruitment", "100% regulatory compliance rate"]
    },
    {
      title: "Biotech Research Data Management",
      challenge: "Biotech startup required scalable solution for managing complex genomic research data",
      solution: "Built custom data lake with advanced analytics and machine learning capabilities",
      results: ["10x improvement in data processing speed", "25% reduction in research time", "Enhanced collaboration across teams"]
    }
  ];

  const industries = [
    { name: "Pharmaceutical", percentage: 40 },
    { name: "Biotechnology", percentage: 30 },
    { name: "Medical Devices", percentage: 20 },
    { name: "Research Institutions", percentage: 10 }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-green-100">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-20 relative overflow-hidden">
        <div className="relative h-96 md:h-[500px] bg-gradient-to-br from-green-600 to-emerald-600">
          <div className="absolute inset-0 bg-black/20"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-green-600/80 to-emerald-600/60"></div>
          
          <div className="absolute bottom-0 left-0 right-0 p-8 md:p-16">
            <div className="max-w-7xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="space-y-6"
              >
                <div className="flex items-center space-x-4 text-white/80 text-sm font-body">
                  <Link 
                    to="/expertise"
                    className="flex items-center space-x-2 hover:text-white transition-colors duration-300"
                  >
                    <FiArrowLeft size={16} />
                    <span>Back to Expertise</span>
                  </Link>
                  <span>•</span>
                  <span className="px-3 py-1 bg-white/20 rounded-full font-accent tracking-wide">
                    Research & Development
                  </span>
                </div>
                
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                    <FiActivity className="text-white" size={32} />
                  </div>
                  <div>
                    <h1 className="text-4xl md:text-6xl font-bold text-white leading-tight font-display tracking-tight">
                      Life Sciences
                    </h1>
                    <p className="text-xl text-green-100 font-body font-light tracking-wide">
                      Accelerating Scientific Discovery & Innovation
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <span className="inline-block px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-medium mb-6 font-accent tracking-wide">
                Scientific Innovation
              </span>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 font-display tracking-tight">
                Advancing
                <span className="block bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                  Scientific Research
                </span>
              </h2>
              <p className="text-xl text-gray-600 mb-8 font-body font-light leading-relaxed">
                We empower life sciences organizations with cutting-edge technology solutions that accelerate research, 
                ensure regulatory compliance, and drive breakthrough discoveries in pharmaceuticals, biotechnology, and medical devices.
              </p>
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center p-4 bg-white rounded-2xl shadow-lg border border-green-100">
                  <div className="text-3xl font-bold text-green-600 font-display">75+</div>
                  <div className="text-sm text-gray-600 font-body">Clinical Trials</div>
                </div>
                <div className="text-center p-4 bg-white rounded-2xl shadow-lg border border-green-100">
                  <div className="text-3xl font-bold text-emerald-600 font-display">25+</div>
                  <div className="text-sm text-gray-600 font-body">Research Institutions</div>
                </div>
                <div className="text-center p-4 bg-white rounded-2xl shadow-lg border border-green-100">
                  <div className="text-3xl font-bold text-green-600 font-display">200+</div>
                  <div className="text-sm text-gray-600 font-body">Regulatory Submissions</div>
                </div>
                <div className="text-center p-4 bg-white rounded-2xl shadow-lg border border-green-100">
                  <div className="text-3xl font-bold text-emerald-600 font-display">98%</div>
                  <div className="text-sm text-gray-600 font-body">Compliance Rate</div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              {[
                { icon: FiSettings, title: "Laboratory Automation", desc: "Streamlining lab workflows and reducing manual errors" },
                { icon: FiCheckCircle, title: "Regulatory Compliance", desc: "Ensuring adherence to FDA, EMA, and GxP standards" },
                { icon: FiBarChart2, title: "Data Analytics", desc: "Advanced analytics for research insights and decision-making" }
              ].map((benefit, index) => (
                <div key={index} className="flex items-start space-x-4 p-6 bg-white rounded-2xl shadow-lg border border-green-100">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <benefit.icon className="text-white" size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2 font-heading tracking-wide">
                      {benefit.title}
                    </h3>
                    <p className="text-gray-600 font-body leading-relaxed">
                      {benefit.desc}
                    </p>
                  </div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Industry Breakdown */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 font-display tracking-tight">
              Industry
              <span className="block bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                Focus Areas
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-body font-light tracking-wide leading-relaxed">
              Our expertise spans across multiple life sciences sectors, delivering specialized solutions for each industry's unique needs.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {industries.map((industry, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                viewport={{ once: true }}
                className="text-center p-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-3xl border border-green-100"
              >
                <div className="relative w-24 h-24 mx-auto mb-4">
                  <svg className="w-24 h-24 transform -rotate-90" viewBox="0 0 36 36">
                    <path
                      d="m18,2.0845 a 15.9155,15.9155 0 0,1 0,31.831 a 15.9155,15.9155 0 0,1 0,-31.831"
                      fill="none"
                      stroke="#e5e7eb"
                      strokeWidth="2"
                    />
                    <path
                      d="m18,2.0845 a 15.9155,15.9155 0 0,1 0,31.831 a 15.9155,15.9155 0 0,1 0,-31.831"
                      fill="none"
                      stroke="url(#greenGradient)"
                      strokeWidth="2"
                      strokeDasharray={`${industry.percentage}, 100`}
                    />
                    <defs>
                      <linearGradient id="greenGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#059669" />
                        <stop offset="100%" stopColor="#10b981" />
                      </linearGradient>
                    </defs>
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-2xl font-bold text-green-600 font-display">{industry.percentage}%</span>
                  </div>
                </div>
                <h3 className="text-lg font-bold text-gray-900 font-heading tracking-wide">
                  {industry.name}
                </h3>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section className="py-20 bg-gradient-to-br from-green-50 to-emerald-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 font-display tracking-tight">
              Life Sciences
              <span className="block bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                Solutions
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-body font-light tracking-wide leading-relaxed">
              Specialized technology solutions designed to accelerate research, ensure compliance, and drive innovation in life sciences.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {solutions.map((solution, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                viewport={{ once: true }}
                className="group p-8 bg-white rounded-3xl border border-green-100 hover:shadow-2xl transition-all duration-500"
              >
                <div className="flex items-start space-x-4 mb-6">
                  <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <solution.icon className="text-white" size={28} />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3 font-heading tracking-wide">
                      {solution.title}
                    </h3>
                    <p className="text-gray-700 font-body leading-relaxed">
                      {solution.description}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  {solution.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full"></div>
                      <span className="text-sm text-gray-700 font-body">{feature}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 font-display tracking-tight">
              Research
              <span className="block bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                Success Stories
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-body font-light tracking-wide leading-relaxed">
              Real-world examples of how our solutions have accelerated scientific discovery and research outcomes.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-8">
            {caseStudies.map((study, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2, duration: 0.8 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-green-50 to-emerald-50 p-8 rounded-3xl border border-green-100"
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-4 font-heading tracking-wide">
                  {study.title}
                </h3>
                
                <div className="space-y-6">
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2 font-heading tracking-wide">Challenge</h4>
                    <p className="text-gray-600 font-body leading-relaxed">{study.challenge}</p>
                  </div>
                  
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2 font-heading tracking-wide">Solution</h4>
                    <p className="text-gray-600 font-body leading-relaxed">{study.solution}</p>
                  </div>
                  
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-3 font-heading tracking-wide">Results</h4>
                    <div className="space-y-2">
                      {study.results.map((result, idx) => (
                        <div key={idx} className="flex items-center space-x-3">
                          <div className="w-2 h-2 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full"></div>
                          <span className="text-gray-700 font-body">{result}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-green-600 to-emerald-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-white/5"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-display tracking-tight">
              Ready to Accelerate Your Research?
            </h2>
            <p className="text-xl text-green-100 max-w-3xl mx-auto font-body font-light tracking-wide leading-relaxed">
              Let's discuss how our life sciences expertise can help you drive breakthrough discoveries and bring innovations to market faster.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-white text-green-600 px-8 py-4 rounded-full font-semibold text-lg shadow-xl hover:shadow-2xl transition-all duration-300 font-accent tracking-wide"
                >
                  Start Your Research Project
                </motion.button>
              </Link>
              <Link to="/contact">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="border-2 border-white text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-white hover:text-green-600 transition-all duration-300 font-accent tracking-wide"
              >
                Schedule Consultation
              </motion.button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LifeSciences; 